/**
 * Pure resolution logic for phone-number feature cohorts.
 *
 * THE SEMANTICS LIVE IN TWO PLACES — this file and the bot's
 * `bot/services/feature_cohorts.py` — both pinned to ONE shared test-vector
 * table (`resolve.test.ts` / `tests/test_feature_cohorts_resolver.py`).
 * Change the vectors first if the semantics ever change.
 *
 * Precedence: deny override > explicit grant > implied via a granted parent > off.
 * Member rows are environment-scoped: 'all' applies everywhere, 'staging' /
 * 'prod' only in that bot environment. Implications are one level deep
 * (admin → real_estate), never recursive.
 */

import type {
  FeatureCohort,
  FeatureCohortImplication,
  FeatureCohortMember,
} from "../types";

export type CohortEnvironment = "all" | "staging" | "prod";

type MemberLike = Pick<
  FeatureCohortMember,
  "cohort_slug" | "mode" | "environment"
> &
  Partial<Pick<FeatureCohortMember, "source">>;
type ImplicationLike = Pick<
  FeatureCohortImplication,
  "parent_slug" | "child_slug"
>;

const rowsFor = (
  rows: MemberLike[],
  slug: string,
  environment: string,
): MemberLike[] =>
  rows.filter(
    (row) =>
      row.cohort_slug === slug &&
      ((row.environment ?? "all") === "all" || row.environment === environment),
  );

const hasMode = (rows: MemberLike[], mode: "grant" | "deny"): boolean =>
  rows.some((row) => (row.mode ?? "grant") === mode);

export const isEffective = (
  slug: string,
  memberRows: MemberLike[],
  implications: ImplicationLike[],
  environment: string,
): boolean => {
  const rows = rowsFor(memberRows, slug, environment);
  if (hasMode(rows, "deny")) {
    return false;
  }
  if (hasMode(rows, "grant")) {
    return true;
  }
  for (const implication of implications) {
    if (implication.child_slug !== slug) {
      continue;
    }
    const parentRows = rowsFor(
      memberRows,
      implication.parent_slug,
      environment,
    );
    // A denied parent grants nothing (vector v7b).
    if (hasMode(parentRows, "deny")) {
      continue;
    }
    if (hasMode(parentRows, "grant")) {
      return true;
    }
  }
  return false;
};

export type FlagDetail = {
  slug: string;
  name: string;
  selfService: boolean;
  effective: boolean;
  via: "grant" | "implied" | null;
  denied: boolean;
  selfDeniedHere: boolean;
};

export const flagDetail = (
  cohort: Pick<FeatureCohort, "slug" | "name" | "self_service">,
  memberRows: MemberLike[],
  implications: ImplicationLike[],
  environment: string,
): FlagDetail => {
  const rows = rowsFor(memberRows, cohort.slug, environment);
  const denied = hasMode(rows, "deny");
  const granted = hasMode(rows, "grant");
  let implied = false;
  for (const implication of implications) {
    if (implication.child_slug !== cohort.slug) {
      continue;
    }
    const parentRows = rowsFor(
      memberRows,
      implication.parent_slug,
      environment,
    );
    if (hasMode(parentRows, "deny")) {
      continue;
    }
    if (hasMode(parentRows, "grant")) {
      implied = true;
      break;
    }
  }
  const effective = !denied && (granted || implied);
  const selfDeniedHere = rows.some(
    (row) =>
      row.mode === "deny" &&
      row.source === "self_service" &&
      row.environment === environment,
  );
  return {
    slug: cohort.slug,
    name: cohort.name || cohort.slug,
    selfService: cohort.self_service ?? true,
    effective,
    via: granted ? "grant" : implied ? "implied" : null,
    denied,
    selfDeniedHere,
  };
};

/** Phones effectively in `slug` ONLY via a parent implication (no own grant/deny). */
export const impliedMemberPhones = (
  slug: string,
  memberRows: MemberLike[] & { phone_digits?: string }[],
  implications: ImplicationLike[],
  environment: string,
): string[] => {
  const rows = memberRows as Array<MemberLike & { phone_digits: string }>;
  const phones = [...new Set(rows.map((row) => row.phone_digits))];
  return phones.filter((phone) => {
    const phoneRows = rows.filter((row) => row.phone_digits === phone);
    const own = rowsFor(phoneRows, slug, environment);
    if (own.length > 0) {
      return false; // explicit grant or deny — shown in its own group
    }
    return isEffective(slug, phoneRows, implications, environment);
  });
};
